import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, storage } from './firebase'; 
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import * as XLSX from 'xlsx';
import * as Icons from 'react-icons/fa';
import { 
  FaCloudUploadAlt, FaDatabase, FaFileExcel, FaFileCsv, 
  FaTrash, FaDownload, FaPaperPlane, FaSearch, FaFilter, 
  FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaSortAmountDown 
} from 'react-icons/fa';

// --- ENTERPRISE THEME ---
const THEME = {
  primary: '#0f172a',
  accent: '#3b82f6',    // Blue
  success: '#10b981',   // Green
  warning: '#f59e0b',   // Orange
  error: '#ef4444',     // Red
  bg: '#f8fafc',
  card: '#ffffff',
  textMain: '#1e293b',
  textSub: '#64748b',
  border: '#e2e8f0'
};

function DatabaseManager() {
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Upload & Edit State
  const [dbName, setDbName] = useState('');
  const [rawFile, setRawFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [stats, setStats] = useState({ original: 0, cleaned: 0, duplicates: 0, invalid: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date', 'size', 'name'

  useEffect(() => { fetchDatabases(); }, []);

  const fetchDatabases = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const q = query(collection(db, `businesses/${user.uid}/databases`), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      setDatabases(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) { console.error("Error:", error); } 
    finally { setLoading(false); }
  };

  // --- 1. DATA ENGINE (UNCHANGED LOGIC, ENHANCED STATS) ---
  const processAndCleanData = (rawArray) => {
    const originalCount = rawArray.length;
    let validArray = [];
    let invalidCount = 0;

    rawArray.forEach(item => {
      if (!item) return;
      let numStr = String(item).replace(/\D/g, ''); // Strip non-digits
      
      // Auto-fix India (10 digits -> 91+10 digits)
      if (numStr.length === 10) numStr = '91' + numStr;
      
      // Validation Rule: Must be 10-15 digits
      if (numStr.length >= 10 && numStr.length <= 15) {
        validArray.push(numStr);
      } else {
        invalidCount++;
      }
    });

    const uniqueArray = [...new Set(validArray)];
    const duplicatesCount = validArray.length - uniqueArray.length;

    setParsedData(uniqueArray);
    setStats({
      original: originalCount,
      cleaned: uniqueArray.length,
      duplicates: duplicatesCount,
      invalid: invalidCount
    });
  };

  // --- 2. FILE HANDLERS (DRAG & DROP) ---
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]);
  };

  const handleFile = (file) => {
    setRawFile(file);
    const reader = new FileReader();
    
    // Excel Support
    if (file.name.match(/\.(xlsx|xls)$/)) {
      reader.onload = (evt) => {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        processAndCleanData(data.flat());
      };
      reader.readAsBinaryString(file);
    } 
    // CSV/TXT Support
    else if (file.name.match(/\.(csv|txt)$/)) {
      reader.onload = (evt) => {
        const dataArray = evt.target.result.split(/[\n,]+/);
        processAndCleanData(dataArray);
      };
      reader.readAsText(file);
    } else {
      alert("⚠️ Unsupported file. Use .xlsx, .csv, or .txt");
    }
  };

  // --- 3. CLOUD UPLOAD ---
  const handleSaveToCloud = async (e) => {
    e.preventDefault();
    const user = auth.currentUser;
    if (!user || !dbName.trim() || parsedData.length === 0) return;

    setIsUploading(true);
    try {
      const csvContent = parsedData.join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const uniqueFileName = `${Date.now()}_${dbName.replace(/\s+/g, '_')}.csv`;
      const fileRef = ref(storage, `businesses/${user.uid}/databases/${uniqueFileName}`);
      
      await uploadBytes(fileRef, blob);
      const downloadURL = await getDownloadURL(fileRef);

      await addDoc(collection(db, `businesses/${user.uid}/databases`), {
        name: dbName,
        fileName: uniqueFileName,
        storagePath: fileRef.fullPath,
        fileUrl: downloadURL,
        size: (blob.size / 1024).toFixed(2) + ' KB',
        contactsCount: parsedData.length,
        createdAt: serverTimestamp()
      });
      
      setDbName(''); setRawFile(null); setParsedData([]);
      fetchDatabases();
      alert("✅ Database Secured in Cloud Vault!");
    } catch (error) { alert("Upload Failed."); }
    setIsUploading(false);
  };

  const handleDelete = async (dbId, storagePath) => {
    if (!window.confirm("Permanently delete this list?")) return;
    try {
      await deleteObject(ref(storage, storagePath));
      await deleteDoc(doc(db, `businesses/${auth.currentUser.uid}/databases`, dbId));
      fetchDatabases();
    } catch (error) { alert("Delete failed."); }
  };

  // --- 4. SORTING & FILTERING ---
  const filteredDatabases = useMemo(() => {
    let result = databases.filter(d => d.name.toLowerCase().includes(searchTerm.toLowerCase()));
    if (sortBy === 'size') result.sort((a, b) => b.contactsCount - a.contactsCount);
    else if (sortBy === 'name') result.sort((a, b) => a.name.localeCompare(b.name));
    // Default is date (from firestore query)
    return result;
  }, [databases, searchTerm, sortBy]);

  // --- SUB-COMPONENTS ---
  const StatBadge = ({ icon, label, value, color }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'white', padding: '10px 15px', borderRadius: '12px', border: `1px solid ${THEME.border}`, flex: 1 }}>
      <div style={{ color: color, fontSize: '18px' }}>{icon}</div>
      <div>
        <div style={{ fontSize: '10px', color: THEME.textSub, fontWeight: '700', textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: '16px', fontWeight: '800', color: THEME.textMain }}>{value}</div>
      </div>
    </div>
  );

  if (loading) return <div style={{ padding: '50px', textAlign: 'center' }}>Loading Vault...</div>;

  return (
    <div style={{ fontFamily: '"Inter", sans-serif', background: THEME.bg, minHeight: '100vh', padding: '30px' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: THEME.textMain, margin: 0 }}>Audience Vault</h1>
          <p style={{ margin: '5px 0 0 0', color: THEME.textSub, fontSize: '14px' }}>Securely manage, clean, and store your customer lists.</p>
        </div>
        <div style={{ background: 'white', padding: '8px 15px', borderRadius: '10px', border: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaDatabase style={{ color: THEME.accent }} />
          <span style={{ fontWeight: 'bold', color: THEME.textMain }}>{databases.length} Lists Stored</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        
        {/* --- LEFT: UPLOAD ENGINE --- */}
        <div style={{ flex: '1', minWidth: '400px' }}>
          
          {/* DRAG & DROP ZONE */}
          <div 
            onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
            style={{ 
              background: dragActive ? '#eff6ff' : 'white', 
              border: `2px dashed ${dragActive ? THEME.accent : THEME.border}`, 
              borderRadius: '20px', padding: '40px', textAlign: 'center', transition: 'all 0.2s', cursor: 'pointer', marginBottom: '20px'
            }}
          >
            <input id="file-upload" type="file" accept=".csv, .txt, .xlsx, .xls" onChange={handleFileSelect} style={{ display: 'none' }} />
            <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
              <div style={{ width: '60px', height: '60px', background: '#f1f5f9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px' }}>
                <FaCloudUploadAlt size={30} color={THEME.accent} />
              </div>
              <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', color: THEME.textMain }}>Click or Drag file to upload</h3>
              <p style={{ margin: 0, fontSize: '13px', color: THEME.textSub }}>Supports .XLSX, .CSV, .TXT</p>
            </label>
          </div>

          {/* ANALYSIS STATS (Shows only after upload) */}
          {parsedData.length > 0 && (
            <div style={{ animation: 'fadeIn 0.5s ease' }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <StatBadge icon={<FaSortAmountDown />} label="Original" value={stats.original} color={THEME.textSub} />
                <StatBadge icon={<FaTimesCircle />} label="Duplicates" value={stats.duplicates} color={THEME.warning} />
                <StatBadge icon={<FaExclamationTriangle />} label="Invalid" value={stats.invalid} color={THEME.error} />
                <StatBadge icon={<FaCheckCircle />} label="Clean Ready" value={stats.cleaned} color={THEME.success} />
              </div>

              {/* SAVE FORM */}
              <div style={{ background: 'white', padding: '25px', borderRadius: '16px', border: `1px solid ${THEME.border}`, marginBottom: '20px' }}>
                <form onSubmit={handleSaveToCloud}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: THEME.textSub, marginBottom: '8px', textTransform: 'uppercase' }}>List Name</label>
                  <input 
                    required type="text" placeholder="e.g. March 2026 VIPs" 
                    value={dbName} onChange={(e) => setDbName(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `1px solid ${THEME.border}`, marginBottom: '15px', outline: 'none', fontSize: '14px' }}
                  />
                  <button 
                    type="submit" disabled={isUploading}
                    style={{ width: '100%', background: THEME.primary, color: 'white', border: 'none', padding: '14px', borderRadius: '10px', fontWeight: 'bold', cursor: isUploading ? 'not-allowed' : 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)' }}
                  >
                    {isUploading ? 'Encrypting & Saving...' : <><FaDatabase /> Save to Vault</>}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>

        {/* --- RIGHT: DATABASE LIST --- */}
        <div style={{ flex: '1.5', minWidth: '400px', background: 'white', borderRadius: '20px', border: `1px solid ${THEME.border}`, display: 'flex', flexDirection: 'column', height: '600px', overflow: 'hidden' }}>
          
          {/* TOOLBAR */}
          <div style={{ padding: '20px', borderBottom: `1px solid ${THEME.border}`, background: '#f8fafc', display: 'flex', gap: '15px' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <FaSearch style={{ position: 'absolute', left: '12px', top: '12px', color: THEME.textSub }} />
              <input 
                type="text" placeholder="Search lists..." 
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '10px 10px 10px 35px', borderRadius: '8px', border: `1px solid ${THEME.border}`, outline: 'none' }}
              />
            </div>
            <select onChange={e => setSortBy(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: `1px solid ${THEME.border}`, color: THEME.textSub, outline: 'none', cursor: 'pointer' }}>
              <option value="date">Sort: Date</option>
              <option value="size">Sort: Size</option>
              <option value="name">Sort: Name</option>
            </select>
          </div>

          {/* LIST */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filteredDatabases.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px', color: THEME.textSub }}>No databases found.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr style={{ textAlign: 'left', color: THEME.textSub, fontSize: '11px', textTransform: 'uppercase', borderBottom: `1px solid ${THEME.border}` }}>
                    <th style={{ padding: '15px 20px' }}>Name</th>
                    <th style={{ padding: '15px 20px' }}>Size</th>
                    <th style={{ padding: '15px 20px' }}>Contacts</th>
                    <th style={{ padding: '15px 20px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDatabases.map(db => (
                    <tr key={db.id} style={{ borderBottom: `1px solid ${THEME.border}`, transition: 'background 0.1s' }} className="hover-row">
                      <td style={{ padding: '15px 20px' }}>
                        <div style={{ fontWeight: '600', color: THEME.textMain }}>{db.name}</div>
                        <div style={{ fontSize: '11px', color: THEME.textSub }}>{db.createdAt?.seconds ? new Date(db.createdAt.seconds * 1000).toLocaleDateString() : 'Just now'}</div>
                      </td>
                      <td style={{ padding: '15px 20px', fontSize: '13px', color: THEME.textSub }}>{db.size}</td>
                      <td style={{ padding: '15px 20px' }}>
                        <span style={{ background: '#ecfdf5', color: '#059669', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                          {db.contactsCount}
                        </span>
                      </td>
                      <td style={{ padding: '15px 20px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
                          <a href={db.fileUrl} target="_blank" rel="noreferrer" style={{ color: THEME.textSub, cursor: 'pointer' }} title="Download"><FaDownload /></a>
                          <a href="/broadcast" style={{ color: THEME.accent, cursor: 'pointer' }} title="Use in Broadcast"><FaPaperPlane /></a>
                          <button onClick={() => handleDelete(db.id, db.storagePath)} style={{ color: THEME.error, border: 'none', background: 'none', cursor: 'pointer' }} title="Delete"><FaTrash /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default DatabaseManager;